import { Box } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { styled } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useUpdateDeviceDevicesIdPatchMutation } from "api";
import { INonGenericDevice } from "interfaces/device.interface";
import {
  toastErrorPositionCenter,
  toastSuccessPositionCenter,
} from "utils/toast.util";

import {
  DeviceFormSchema,
  deviceFormSchema,
  setDefaults,
} from "./deviceFormSchema";

import { FormActions } from "../../components/FormActions";
import { PageHeader } from "../../components/PageHeader";
import { DeviceInformation } from "../components/DeviceInformation";
import { DeviceSettings } from "../components/DeviceSettings";
import { SubjectDropDown } from "../components/SubjectDropDown";

const Wrapper = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr 300px",
  gridTemplateAreas:
    "'title title' 'info subject' 'settings settings' 'actions actions'",
  [theme.breakpoints.down("sm")]: {
    gridTemplateAreas: "'title' 'info' 'subject' 'settings' 'actions'",
  },
})) as typeof Box;

type DeviceFormProp = {
  device: INonGenericDevice;
};

export const DeviceForm = ({ device }: DeviceFormProp) => {
  const { t } = useTranslation();

  const { control, handleSubmit, setValue, resetField, reset } =
    useForm<DeviceFormSchema>({
      resolver: zodResolver(deviceFormSchema),
      defaultValues: deviceFormSchema.parse(setDefaults(device)),
    });

  const [updateDevice, { isLoading }] = useUpdateDeviceDevicesIdPatchMutation();

  const onSubmit = handleSubmit(async (d) => {
    try {
      const response = await updateDevice({
        id: device.id,
        deviceUpdate: { ...d, subject_id: d.subject_id || null },
      });

      const updatedDevice = response.data?.data ?? null;

      if (updatedDevice && updatedDevice.type !== "generic") {
        reset(deviceFormSchema.parse(updatedDevice));
      }

      toastSuccessPositionCenter(t("Device information updated successfully"));
    } catch {
      toastErrorPositionCenter(t("Try again"));
    }
  });

  return (
    <Wrapper component={"form"} onSubmit={onSubmit}>
      <PageHeader title={device.name ?? ""} gridArea={"title"} />
      <DeviceInformation
        control={control}
        setValue={setValue}
        resetField={resetField}
      />
      <SubjectDropDown deviceId={device.id} control={control} />
      <DeviceSettings deviceType={device.type} control={control} />
      <FormActions
        submitText={t("Save")}
        backText={t("Cancel")}
        backHref={"../devices"}
        isLoading={isLoading}
        gridArea={"actions"}
      />
    </Wrapper>
  );
};
